import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Controller } from "react-hook-form";
import ComboboxInput from "../inputs/ComboboxInput";
import TextInput from "../inputs/TextInput";
import { mapToOptions } from "../../lib/functions";
import {
  useGetAllAccessGroupsDD,
  useGetAllBuildingsById,
  useGetAllDepartmentsById,
  useGetAllGatesById,
  useGetAllHosts,
  useGetAllLocationsDropdown,
  useGetAllMeetingRoomsByIdDD,
} from "../../hooks/common/query";
import { useGetAllVisitRequestTypes } from "../../hooks/visitor/query";
import DateTimePickerInput from "../inputs/DateTimePickerInput";
import Accordion from "../accordian";
import { useTranslation } from "react-i18next";
import {
  STATUS_IDS,
  USER_ROLES_IDS,
  UserAppointment,
  VISIT_REQUEST_VIEW_TYPE,
  VisitorType,
} from "../../constants/common";
import { useLocation } from "react-router-dom";
import { APP_ROUTES } from "../../routes";
import useAuthStore from "../../store/Auth";
import CheckboxInput from "../inputs/CheckboxInput";
import toast from "react-hot-toast";

interface VisitDetailsProps {
  control: any;
  errors: any;
  clearErrors: any;
  userAppointmentDetails?: UserAppointment;
  cardNumber?: number;
  isVIP?: boolean;
  isMobileAllowed?: boolean;
  setIsMobileAllowed?: Dispatch<SetStateAction<boolean>>;
  isLaptopAllowed?: boolean;
  setIsLaptopAllowed?: Dispatch<SetStateAction<boolean>>;

  visitStartDate: Date;
  visitEndDate?: Date;

  setValue: any;

  visitPurposes?: any[];
  isVisitPurposeOther?: boolean;
  nextApproverRoleId?: number;

  isDeviceDetails?: boolean;
  setIsDeviceDetails?: Dispatch<SetStateAction<boolean>>;

  isVehicleInfoVisible?: boolean;
  setIsVehicleInfoVisible?: Dispatch<SetStateAction<boolean>>;

  visitAllowedDuration?: number;

  viewType?: string | null;
}

const VisitDetailsCard = ({
  control,
  errors,
  isVIP,
  userAppointmentDetails,
  clearErrors,
  cardNumber = 3,

  visitStartDate,
  visitEndDate,

  setValue,

  visitPurposes,
  isVisitPurposeOther,
  nextApproverRoleId,

  isDeviceDetails,
  setIsDeviceDetails,

  isVehicleInfoVisible,
  setIsVehicleInfoVisible,

  visitAllowedDuration,

  viewType,
}: VisitDetailsProps) => {
  const [selectedKey, setSelectedKey] = useState<string>("1");

  const [selectedLocationId, setSelectedLocationId] = useState<string | number>(
    0
  );

  const [selectedBuildingId, setSelectedBuildingId] = useState<string | number>(
    0
  );

  const [, setSelectedMeetingRoomId] = useState<string | number>(0);

  const location = useLocation();

  const [maxEndDate, setMaxEndDate] = useState<Date>();

  const { data: allLocations } = useGetAllLocationsDropdown();
  const { data: visitRequestTypes } = useGetAllVisitRequestTypes();
  const { data: hosts } = useGetAllHosts();
  const { data: allAccessGroups } = useGetAllAccessGroupsDD({
    encryptedLocationID: selectedLocationId
      ? selectedLocationId.toString()
      : userAppointmentDetails?.location
      ? userAppointmentDetails?.location.toString()
      : "",
    n_BuildingID: selectedBuildingId
      ? selectedBuildingId.toString()
      : userAppointmentDetails?.building
      ? userAppointmentDetails?.building.toString()
      : "",
  });

  const user = useAuthStore((state) => state.user);

  useEffect(() => {
    if (maxEndDate && setValue) {
      if (visitAllowedDuration && !userAppointmentDetails) {
        if (visitEndDate && new Date(visitEndDate) > maxEndDate) {
          setValue("visitEndDate", maxEndDate);
        }
      }
    }
  }, [
    maxEndDate,
    setValue,
    userAppointmentDetails,
    visitAllowedDuration,
    visitEndDate,
  ]);

  const isStatusCanceledExpiredRejected =
    userAppointmentDetails?.approvalStatus === STATUS_IDS.CANCELED ||
    userAppointmentDetails?.approvalStatus === STATUS_IDS.EXPIRED ||
    userAppointmentDetails?.approvalStatus === STATUS_IDS.REJECTED;

  const isReadonly = useMemo(() => {
    const role = user?.n_RoleID;
    // if (
    //   location.pathname === APP_ROUTES.APPROVE_REQUEST &&
    //   isStatusCanceledExpiredRejected
    // ) {
    //   return true;
    // }
    if (role === USER_ROLES_IDS.STAFF) return false;
    if (
      viewType !== VISIT_REQUEST_VIEW_TYPE.VIEW_ONLY &&
      (role === USER_ROLES_IDS.SECURITY ||
        role === USER_ROLES_IDS.SECURITY_MANAGER)
    ) {
      return nextApproverRoleId !== user?.n_RoleID;
    }
    return true;
  }, [user?.n_RoleID, viewType, nextApproverRoleId]);

  const hostNames = useMemo(
    () => mapToOptions("fullName", "fullName", "email", hosts),
    [hosts]
  );

  const accessGroups = useMemo(
    () =>
      mapToOptions(
        "s_AccessGroupName_En",
        "s_AccessGroupName_Ar",
        "n_AccessGroupID",
        allAccessGroups
      ),
    [allAccessGroups]
  );

  const { t, i18n } = useTranslation();

  const { data: allDepartments } = useGetAllDepartmentsById({
    encryptedDepartmentID: "0",
    encryptedLocationID: selectedLocationId
      ? selectedLocationId.toString()
      : userAppointmentDetails?.location
      ? userAppointmentDetails?.location.toString()
      : "",
    n_BuildingID: selectedBuildingId
      ? selectedBuildingId.toString()
      : userAppointmentDetails?.building
      ? userAppointmentDetails?.building.toString()
      : "",
  });

  const { data: allGates } = useGetAllGatesById({
    n_LocationID: selectedLocationId
      ? selectedLocationId.toString()
      : userAppointmentDetails?.location
      ? userAppointmentDetails?.location.toString()
      : "",
    n_BuildingID: selectedBuildingId
      ? selectedBuildingId.toString()
      : userAppointmentDetails?.building
      ? userAppointmentDetails?.building.toString()
      : "",
  });

  const { data: allMeetingRooms } = useGetAllMeetingRoomsByIdDD({
    encryptedMeetingRoomID: "",
    encryptedLocationID: selectedLocationId
      ? selectedLocationId.toString()
      : userAppointmentDetails?.location
      ? userAppointmentDetails?.location.toString()
      : "",
    n_BuildingID: selectedBuildingId
      ? selectedBuildingId.toString()
      : userAppointmentDetails?.building
      ? userAppointmentDetails?.building.toString()
      : "",
  });

  const meetingRooms = useMemo(
    () =>
      mapToOptions(
        "s_MeetingRoomName_En",
        "s_MeetingRoomName_Ar",
        "n_MeetingRoomID",
        allMeetingRooms
      ),
    [allMeetingRooms]
  );

  const { data: allBuildings } = useGetAllBuildingsById({
    encryptedId: selectedLocationId
      ? selectedLocationId.toString()
      : userAppointmentDetails?.location
      ? userAppointmentDetails?.location.toString()
      : "",
  });

  const isApprovalReq = useMemo(
    () => location.pathname === APP_ROUTES.APPROVE_REQUEST,
    [location.pathname]
  );

  useEffect(() => {
    if (visitStartDate) {
      const maxDate = new Date(visitStartDate);
      if (
        visitAllowedDuration &&
        (!userAppointmentDetails ||
          userAppointmentDetails?.approvalStatus === STATUS_IDS.PENDING ||
          userAppointmentDetails?.approvalStatus === STATUS_IDS.REQUEST_INFO)
      ) {
        maxDate.setDate(maxDate.getDate() + visitAllowedDuration);
      } else {
        maxDate.setDate(maxDate.getDate());
      }
      setMaxEndDate(maxDate);
    }
  }, [
    userAppointmentDetails,
    userAppointmentDetails?.approvalStatus,
    visitAllowedDuration,
    visitStartDate,
  ]);

  const buildings = useMemo(
    () =>
      allBuildings
        ? mapToOptions(
            "s_BuildingName_En",
            "s_BuildingName_Ar",
            "n_BuildingID",
            allBuildings
          )
        : [],
    [allBuildings]
  );

  const departments = useMemo(
    () =>
      mapToOptions(
        "s_DepartmentName_En",
        "s_DepartmentName_Ar",
        "n_DepartmentID",
        allDepartments
      ),
    [allDepartments]
  );

  const gates = useMemo(
    () => mapToOptions("s_GateName_En", "s_GateName_Ar", "n_GateID", allGates),
    [allGates]
  );

  const visitReqTypes = useMemo(
    () =>
      mapToOptions("s_Desc_E", "s_Desc_A", "n_DetailedCode", visitRequestTypes),
    [visitRequestTypes]
  );

  const locations = useMemo(
    () =>
      mapToOptions(
        "s_LocationName_En",
        "s_LocationName_Ar",
        "n_LocationID",
        allLocations
      ),
    [allLocations]
  );

  return (
    <div className="bg-white z-10 p-2 md:p-4 rounded-xl flex flex-col gap-3 lg:gap-6 shadow-[0px_1px_5px_0px_rgba(113,112,163,0.3)]">
      <Accordion
        id="1"
        selectedKey={selectedKey}
        onSelectionChange={setSelectedKey}
        title={
          <h1 className="text-xl md:text-2xl flex flex-1 -mt-2 items-center">
            {t(`${cardNumber}`)}. {t("visitDetails")}
          </h1>
        }
      >
        <div className="flex flex-col gap-2 lg:gap-4">
          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 lg:gap-4">
            <Controller
              name="location"
              control={control}
              render={({ field: { onChange, name, value } }) => (
                <ComboboxInput
                  options={locations}
                  value={
                    locations?.find((item) => item.value === value)?.labelEn ||
                    ""
                  }
                  onChange={(value) => {
                    setValue("building", "");
                    setValue("meetingRoom", "");
                    setValue("gate", "");
                    setValue("visitingDepartment", "");
                    onChange(value);
                    setSelectedLocationId(value);
                    clearErrors(name);
                  }}
                  error={errors?.[name]?.message}
                  label={t("location")}
                  required
                  readOnly={isReadonly}
                />
              )}
            />

            <Controller
              name="building"
              control={control}
              render={({ field: { onChange, name, value } }) => (
                <ComboboxInput
                  options={buildings}
                  value={
                    buildings?.find((item) => item.value === value)?.labelEn ||
                    ""
                  }
                  onChange={(value) => {
                    setValue("meetingRoom", "");
                    setValue("gate", "");
                    setValue("visitingDepartment", "");
                    onChange(value);
                    setSelectedBuildingId(value);
                    clearErrors(name);
                  }}
                  error={errors?.[name]?.message}
                  label={t("building")}
                  required
                  readOnly={isReadonly}
                />
              )}
            />

            <Controller
              name="hostEmail"
              control={control}
              render={({ field: { onChange, name, value } }) => (
                <ComboboxInput
                  options={hostNames}
                  value={
                    hostNames?.find(
                      (item) => item.value.toLowerCase() == value.toLowerCase()
                    )?.labelEn || ""
                  }
                  onChange={(value) => {
                    onChange(value);
                    clearErrors(name);
                  }}
                  error={errors?.[name]?.message}
                  label={t("mofaHostName")}
                  required
                  readOnly
                  isInfoToolTip
                  infoToolTip={<p>{t("enterMofaStaffEmail")}</p>}
                />
              )}
            />

            <Controller
              name="hostMobileNumber"
              control={control}
              render={({ field: { onChange, name, value } }) => (
                <div className="flex flex-col">
                  <TextInput
                    label={`MOFA ${t("hostMobileNumber")}`}
                    value={value}
                    onChange={onChange}
                    error={errors?.[name]?.message}
                    required
                    readOnly={user?.n_RoleID !== USER_ROLES_IDS.STAFF}
                  />
                </div>
              )}
            />

            <Controller
              name="visitingDepartment"
              control={control}
              render={({ field: { onChange, name, value } }) => (
                <ComboboxInput
                  options={departments}
                  value={
                    departments?.find((item) => item.value === value)
                      ?.labelEn || ""
                  }
                  onChange={(value) => {
                    onChange(value);
                    clearErrors(name);
                  }}
                  error={errors?.[name]?.message}
                  label={t("department")}
                  required={!isVIP}
                  readOnly={isReadonly}
                />
              )}
            />

            <Controller
              name="gate"
              control={control}
              render={({ field: { onChange, name, value } }) => (
                <ComboboxInput
                  options={gates}
                  value={
                    gates?.find((item) => item.value === value)?.labelEn || ""
                  }
                  onChange={(value) => {
                    onChange(value);
                    clearErrors(name);
                  }}
                  error={errors?.[name]?.message}
                  label={t("gate")}
                  required={!isVIP}
                  readOnly={isReadonly}
                />
              )}
            />

            <Controller
              name="meetingRoom"
              control={control}
              render={({ field: { onChange, name, value } }) => (
                <ComboboxInput
                  options={meetingRooms}
                  value={
                    meetingRooms?.find((item) => item.value === value)
                      ?.labelEn || ""
                  }
                  onChange={(value) => {
                    onChange(value);
                    setSelectedMeetingRoomId(value);
                    clearErrors(name);
                  }}
                  error={errors?.[name]?.message}
                  label={t("meetingRoom")}
                  readOnly={isReadonly}
                />
              )}
            />

            <Controller
              name="visitReqType"
              control={control}
              render={({ field: { onChange, name, value } }) => (
                <ComboboxInput
                  options={visitReqTypes.filter(
                    (item) => item.value !== Number(VisitorType.VVIP)
                  )}
                  value={
                    i18n.language === "en"
                      ? visitReqTypes?.find(
                          (item) => item.value === Number(value)
                        )?.labelEn
                      : visitReqTypes?.find(
                          (item) => item.value === Number(value)
                        )?.labelAr
                  }
                  onChange={(value) => {
                    onChange(value);
                    clearErrors(name);
                  }}
                  error={errors?.[name]?.message}
                  label={t("visitorType")}
                  required
                  readOnly={isVIP || isReadonly}
                />
              )}
            />

            <Controller
              name="visitPurpose"
              control={control}
              render={({ field: { name, onChange, value } }) => {
                return (
                  <ComboboxInput
                    options={visitPurposes}
                    value={
                      visitPurposes?.find((item) => item.value === value)
                        ?.labelEn || ""
                    }
                    onChange={(value) => {
                      onChange(value);
                      clearErrors(name);
                    }}
                    error={errors?.[name]?.message}
                    label={t("visitPurpose")}
                    required={!isVIP}
                    readOnly={isReadonly}
                  />
                );
              }}
            />

            {/* to change PurposeOtherValue */}
            {isVisitPurposeOther && (
              <Controller
                name="visitPurposeOther"
                control={control}
                render={({ field: { onChange, name, value } }) => (
                  <div className="flex flex-col">
                    <TextInput
                      label={t("visitPurposeOther")}
                      value={value || ""}
                      onChange={onChange}
                      error={errors?.[name]?.message}
                      required
                      readOnly={isReadonly}
                    />
                  </div>
                )}
              />
            )}

            <Controller
              name="visitStartDate"
              control={control}
              render={({ field: { name, value, onChange } }) => (
                <DateTimePickerInput
                  label={t("visitStartDate")}
                  value={value}
                  required
                  onChange={(value) => {
                    if (!value) return;

                    onChange(value);
                    clearErrors(name);

                    if (
                      location.pathname !== APP_ROUTES.UPDATE_REQUEST &&
                      location.pathname !== APP_ROUTES.APPROVE_REQUEST
                    ) {
                      // Set visitEndDate to visitStartDate + 1 hour
                      const newEndDate = new Date(value);
                      newEndDate.setHours(newEndDate.getHours() + 1);
                      setValue("visitEndDate", newEndDate);
                    }
                    const maxDate = new Date(value);
                    if (
                      visitAllowedDuration &&
                      (!userAppointmentDetails ||
                        userAppointmentDetails?.approvalStatus ===
                          STATUS_IDS.PENDING ||
                        userAppointmentDetails?.approvalStatus ===
                          STATUS_IDS.REQUEST_INFO)
                    ) {
                      maxDate.setDate(maxDate.getDate() + visitAllowedDuration);
                    } else {
                      maxDate.setDate(maxDate.getDate());
                    }
                    setMaxEndDate(maxDate);
                  }}
                  error={errors?.[name]?.message}
                  minDate={
                    location.pathname !== APP_ROUTES.APPROVE_REQUEST &&
                    location.pathname !== APP_ROUTES.UPDATE_REQUEST
                      ? new Date()
                      : undefined
                  }
                  readOnly={isReadonly}
                  dateTimeValidation={(newDate: Date) => {
                    if (
                      location.pathname !== APP_ROUTES.APPROVE_REQUEST &&
                      location.pathname !== APP_ROUTES.UPDATE_REQUEST &&
                      newDate < new Date()
                    ) {
                      toast.error(t("visitStartTimeCannotBeInPast"));
                      return false;
                    }
                    return true;
                  }}
                />
              )}
            />

            <Controller
              name="visitEndDate"
              control={control}
              render={({ field: { name, value, onChange } }) => {
                const currentMaxDate =
                  !userAppointmentDetails ||
                  userAppointmentDetails?.approvalStatus ===
                    STATUS_IDS.PENDING ||
                  userAppointmentDetails?.approvalStatus ===
                    STATUS_IDS.REQUEST_INFO
                    ? maxEndDate
                    : undefined;

                return (
                  <DateTimePickerInput
                    label={t("visitEndDate")}
                    value={value}
                    required
                    onChange={(value) => {
                      // Ensure the selected value doesn't exceed maxDate
                      if (
                        currentMaxDate &&
                        value &&
                        new Date(value) > currentMaxDate
                      ) {
                        onChange(currentMaxDate);
                      } else {
                        onChange(value);
                      }
                      clearErrors(name);
                    }}
                    // isInfoToolTip
                    // infoToolTip={<p>{t("visitDurationLimit")}</p>}
                    minDate={
                      // location.pathname !== APP_ROUTES.APPROVE_REQUEST &&
                      // location.pathname !== APP_ROUTES.UPDATE_REQUEST
                      // ?
                      (() => {
                        if (visitStartDate) {
                          const d = new Date(visitStartDate);
                          if (
                            user?.n_RoleID === USER_ROLES_IDS.SECURITY ||
                            user?.n_RoleID === USER_ROLES_IDS.SECURITY_MANAGER
                          ) {
                            d.setDate(d.getDate() + 1);
                          }
                          return d;
                        }
                        return new Date();
                      })()
                      // : undefined
                    }
                    maxDate={
                      // location.pathname !== APP_ROUTES.APPROVE_REQUEST &&
                      // location.pathname !== APP_ROUTES.UPDATE_REQUEST
                      //   ? maxEndDate
                      //   : undefined
                      currentMaxDate
                    }
                    error={errors?.[name]?.message}
                    readOnly={isReadonly}
                    dateTimeValidation={(newDate: Date) => {
                      if (newDate < visitStartDate) {
                        toast.error(t("visitEndTimeLessThanStartTime"));
                        return false;
                      }
                      return true;
                    }}
                  />
                );
              }}
            />

            {(user?.n_RoleID === USER_ROLES_IDS.ADMIN ||
              user?.n_RoleID === USER_ROLES_IDS.SECURITY ||
              user?.n_RoleID === USER_ROLES_IDS.SECURITY_MANAGER) && (
              <Controller
                name="accessGroup"
                control={control}
                render={({ field: { onChange, name, value } }) => (
                  <ComboboxInput
                    options={accessGroups}
                    value={
                      accessGroups?.find((item) => item.value === value)
                        ?.labelEn || ""
                    }
                    onChange={(value) => {
                      onChange(value);
                      clearErrors(name);
                    }}
                    error={errors?.[name]?.message}
                    label={t("accessGroup")}
                    required
                    readOnly={
                      viewType === VISIT_REQUEST_VIEW_TYPE.VIEW_ONLY ||
                      nextApproverRoleId !== user?.n_RoleID ||
                      isStatusCanceledExpiredRejected
                    }
                  />
                )}
              />
            )}
          </div>

          <div className="flex flex-col gap-2 lg:gap-4 ">
            {/* {userAppointmentDetails?.vehicleNo &&
              (user?.n_RoleID === USER_ROLES_IDS.SECURITY ||
                user?.n_RoleID === USER_ROLES_IDS.SECURITY_MANAGER) && (
                <CheckboxInput
                  key="approve-vehicles"
                  id="approve-vehicles"
                  label={t("approveVehicleEntryPermit")}
                  checked={isVehicleAllowed}
                  setChecked={() => setIsVehicleAllowed?.((prev) => !prev)}
                  readOnly={nextApproverRoleId !== user?.n_RoleID}
                />
              )} */}

            <Controller
              name="note"
              control={control}
              render={({ field: { onChange, name, value } }) => (
                <div className="flex flex-col">
                  <TextInput
                    label={
                      !userAppointmentDetails
                        ? t("note")
                        : userAppointmentDetails?.isExternalAppointment
                        ? t("noteFromVisitor")
                        : t("noteFromStaff")
                    }
                    value={value}
                    onChange={onChange}
                    error={errors?.[name]?.message}
                    readOnly={!!isApprovalReq}
                  />
                </div>
              )}
            />
          </div>

          <div className="flex items-center gap-10">
            {location.pathname === APP_ROUTES.GROUP_VISIT_REQUEST ? (
              <CheckboxInput
                id="group-vehicle-details"
                label={t("vehicleDetails")}
                checked={isVehicleInfoVisible}
                setChecked={setIsVehicleInfoVisible}
                readOnly={
                  (user?.n_RoleID !== USER_ROLES_IDS.STAFF &&
                    nextApproverRoleId !== user?.n_RoleID) ||
                  user?.n_RoleID !== USER_ROLES_IDS.STAFF
                }
              />
            ) : (
              <CheckboxInput
                key="remember-me"
                id="remember-me"
                label={t("declareDevicesBroughtOnsite")}
                checked={isDeviceDetails}
                setChecked={() => setIsDeviceDetails?.((prev) => !prev)}
                readOnly={
                  (user?.n_RoleID !== USER_ROLES_IDS.STAFF &&
                    nextApproverRoleId !== user?.n_RoleID) ||
                  user?.n_RoleID !== USER_ROLES_IDS.STAFF
                }
              />
            )}
          </div>
        </div>
      </Accordion>
    </div>
  );
};

export default React.memo(VisitDetailsCard);
